import { Lib, type Tenant } from "albertine-shared-web";
import { addDays, addHours, addMinutes } from "date-fns";
import { useState } from "react";
import DatePicker from "react-datepicker";
import type { Agent } from "../../common/types/Agent";
import { EventEmitter } from "../../EventEmitter";
import {
    AppButton,
    AppDashedLine,
    AppHeading1,
    AppHeading3,
    AppParagraph,
    AppParagraphs,
} from "../App/AppComponents";
import EmojiReaction, { ReactionVariant } from "../Chat/EmojiReaction";
import AlertNotification from "../Common/AlertNotification";
import AppPreview from "../Common/AppPreview/AppPreview";
import Asset, { Assets } from "../Common/Asset";
import {
    AgentAvatar,
    AgentAvatarList,
    AgentAvatarWithSecondary,
    Initials,
    MemberAvatar,
} from "../Common/Avatar";
import BackButton from "../Common/BackButton";
import { MenuButton } from "../Common/Button/MenuButton";
import ButtonPill from "../Common/ButtonPill";
import Button from "../Common/Buttons";
import ChevronRoundButton from "../Common/ChevronRoundButton";
import { FormattedDate, FormattedDateTime } from "../Common/DateTime";
import DeadlineTag, { deadlineToCSSColor } from "../Common/DeadlineTag";
import Divider from "../Common/Divider";
import Dropdown from "../Common/Dropdown";
import EditableInputField from "../Common/EditableInputField";
import EditableTextarea from "../Common/EditableTextarea";
import { Flex, FlexInline } from "../Common/Flex";
import GeneratedOptionsSelector from "../Common/GeneratedOptionsSelector";
import Highlight from "../Common/Highlight";
import { HoverMenu, HoverMenuItem } from "../Common/HoverMenu";
import Icon, { PictogramIcon } from "../Common/Icon";
import ImageGallery from "../Common/ImageGallery";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";
import Input from "../Common/Input";
import Label from "../Common/Label";
import { List } from "../Common/List";
import LocalDateTimePicker from "../Common/LocalDateTimePicker";
import Paragraph from "../Common/Paragraph";
import RenderLinksWithTooltips from "../Common/RenderLinksWithTooltips";
import RoundedSelectionToggle from "../Common/RoundedSelectionToggle";
import MemberRating from "../Common/rating/MemberRating";
import Tag from "../Common/Tag";
import TicketNumber, { TicketNumberSmall } from "../Common/TicketNumber";
import TimeTag from "../Common/TimeTag";
import ToastMessages, {
    ToastMessageInfoView,
    ToastMessageNotificationView,
} from "../Common/ToastMessage";
import Tooltip from "../Common/Tooltip";
import UnreadMessagesIndicator from "../Common/UnreadMessagesIndicator";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import RequestCard from "../RequestListView/RequestCard";
import "./ComponentLibrary.css";
import type { CatalogItem } from "@albertine/types/openapi/lmt";
import classNames from "classnames";
import { ComponentExample } from "@/components/ComponentLibrary/component-example";
import { timestampFromDate } from "../../utils/timestamp.util";
import ArticleSearchInput from "../CatalogBeta/ArticleSearchInput";
import ArticleThumbnail from "../CatalogBeta/ArticleThumbnail";
import MessageAttachmentPreview from "../Chat/MessageAttachmentPreview";
import Hr from "../Common/Hr";
import AutocompleteDropdown from "../Common/Input.AutocompleteDropdown";
import SortableList from "../Common/list/SortableList";
import RequestSnippet from "../Request/RequestSnippet";
import {
    mockAgent,
    mockConfirmedBooking,
    mockRequest,
    mockRequestActive,
    mockRequestClosed,
    mockRequestConfirmed,
    mockRequestDraft,
} from "./ComponentLibrary.util";
import { AutocompleteExampleWithMembers } from "./examples/AutocompleteExamples";
import FormExamples from "./examples/FormExamples";
import OverlayExamples from "./examples/OverlayExamples";
import SearchFilterExamples from "./examples/SearchFilterExamples";
import {
    Block,
    ColorBlock,
    CopyToClipboard,
    GroupBlock,
    ShadowBlock,
    Spacer,
    Subtitle,
    TableOfContents,
    Title,
} from "./LibraryBuildingBlocks";

const SortableExample = SortableList();

const catalogItemExample: CatalogItem = {
    id: "f263ca5d-f811-48ac-8ca3-0bb9468a5b0b",
    lmtId: "ynqQDWBmgxGyUpYq9hON",
    name: "Experimental Cocktail Club Chinatown",
    tags: [],
    offeringCategories: ["nightlife"],
    travelItineraryNotes: null,
    eventDates: null,
    address: "13a Gerrard St, London W1D 5PS, UK",
    addressComponents: [
        {
            longName: "England",
            shortName: "England",
            types: ["administrative_area_level_1"],
        },
        {
            longName: "Greater London",
            shortName: "Greater London",
            types: ["administrative_area_level_2"],
        },
        {
            longName: "United Kingdom",
            shortName: "United Kingdom",
            types: ["country"],
        },
    ],
    locationGeoPoint: { latitude: 51.5119278, longitude: -0.1309422 },
    images: [
        {
            id: "1",
            url: "https://s3.amazonaws.com/a.storyblok.com/f/261676/1200x800/e8e005da2b/795023757-829422762.jpeg",
        },
    ],
    published: true,
    slug: "experimental-cocktail-club-chinatown-104406309",
    discoverableForTenant: null,
};

const fitToSize = {
    width: "auto",
    maxWidth: "100%",
    height: "auto",
    maxHeight: "100%",
};

function FlexRowWrap(props: { children: React.JSX.Element[] }) {
    const { children } = props;
    return (
        <div
            style={{
                display: "flex",
                flexFlow: "wrap",
                gap: "10px",
            }}
        >
            {children}
        </div>
    );
}

function wait(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

// make sure typing fail once new tenants are added
const tenantsMap: Record<Tenant, true> = {
    albertine: true,
    hugoboss: true,
    mastercard: true,
    carlo: true,
    "visa-i2": true,
    revolut: true,
};

const ALL_TENANTS: Tenant[] = Object.keys(tenantsMap) as Tenant[];

const ComponentLibrary = function ComponentLibrary() {
    const [selectedTenant, setSelectedTenant] = useState<Tenant>("albertine");

    const [exampleDate, setExampleDate] = useState<Date | null>();
    const [showLocalDateTimePicker, setShowLocalDateTimePicker] = useState(false);

    return (
        <div className={classNames("component-library", selectedTenant)}>
            <div className="component-library--left">
                <Title>Component Library</Title>

                <Block title="Shared web components" className={selectedTenant}>
                    <Dropdown
                        label="Select tenant"
                        entries={ALL_TENANTS.map((tenant) => [tenant, tenant])}
                        currentValue={selectedTenant}
                        onChange={(value) => {
                            setSelectedTenant(value as Tenant);
                        }}
                    />

                    <Lib.Heading.H1>I am heading 1</Lib.Heading.H1>
                    <Lib.Heading.H1>I am heading 1</Lib.Heading.H1>
                    <Lib.Paragraph.Large.Text3>
                        I am large blue paragraph
                    </Lib.Paragraph.Large.Text3>
                    <Lib.Label.Large.Text3>I am large blue label</Lib.Label.Large.Text3>

                    <Lib.Flex.Column gap="value16">
                        <Lib.Button.Primary label="Primary button" />

                        <Lib.Button.Primary label="Primary button with icon">
                            <Lib.Icon.CalendarMonth.Small.Text />
                        </Lib.Button.Primary>
                    </Lib.Flex.Column>
                </Block>

                <Block title="Text styles">
                    <CopyToClipboard>
                        <h1>Heading 1</h1>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <h2>Heading 2</h2>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <h3>Heading 3</h3>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <h4>Heading 4</h4>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <h5>Heading 5</h5>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <h6>Heading 6</h6>
                    </CopyToClipboard>
                    <Spacer />
                    <Paragraph>
                        Label component is indented to be used for single line texts.
                        Multiple variations available.
                    </Paragraph>
                    <Spacer />
                    <CopyToClipboard>
                        <Label.Large>Label.Large</Label.Large>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Label.Medium>Label.Medium</Label.Medium>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Label.Small>Label.Small</Label.Small>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Label.XSmall>Label.XSmall</Label.XSmall>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Label.XXSmall>Label.XXSmall</Label.XXSmall>
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Label.Medium.SemiBold>Label.Medium.Bold</Label.Medium.SemiBold>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Label.Medium.Normal>Label.Medium.Normal</Label.Medium.Normal>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Label.Medium.Light>Label.Medium.Light</Label.Medium.Light>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Label.Small.N10>Label.Small.N10</Label.Small.N10>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Label.Small.N50>Label.Small.N50</Label.Small.N50>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Label.Small.N60>Label.Small.N60</Label.Small.N60>
                    </CopyToClipboard>
                    <Spacer />
                    <Paragraph>
                        Paragraph component is indented to be used for multiline texts.
                        Multiple variations available.
                    </Paragraph>
                    <Spacer />
                    <CopyToClipboard>
                        <Paragraph.Large>Paragraph.Large</Paragraph.Large>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Paragraph.Medium>Paragraph.Medium</Paragraph.Medium>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Paragraph.Small>Paragraph.Small</Paragraph.Small>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Paragraph.XSmall>Paragraph.XSmall</Paragraph.XSmall>
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Paragraph.Medium.SemiBold>
                            Paragraph.Medium.Bold
                        </Paragraph.Medium.SemiBold>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Paragraph.Medium.Normal>
                            Paragraph.Medium.Normal
                        </Paragraph.Medium.Normal>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Paragraph.Medium.Light>
                            Paragraph.Medium.Light
                        </Paragraph.Medium.Light>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Paragraph.Small.N10>Paragraph.Small.N10</Paragraph.Small.N10>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Paragraph.Small.N50>Paragraph.Small.N50</Paragraph.Small.N50>
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Paragraph.Small.N60>Paragraph.Small.N60</Paragraph.Small.N60>
                    </CopyToClipboard>
                    <Spacer />
                    <Paragraph>
                        Use the Hr component for dividers with different margins.
                    </Paragraph>
                    <CopyToClipboard style={{ width: "100%" }}>
                        <Hr />
                    </CopyToClipboard>
                    <CopyToClipboard style={{ width: "100%" }}>
                        <Hr.Large.N10 />
                    </CopyToClipboard>
                    <CopyToClipboard style={{ width: "100%" }}>
                        <Hr.Medium.N20 />
                    </CopyToClipboard>
                    <CopyToClipboard style={{ width: "100%" }}>
                        <Hr.Small.N50 />
                    </CopyToClipboard>
                    <CopyToClipboard style={{ width: "100%" }}>
                        <Hr.XSmall.N60 />
                    </CopyToClipboard>
                    <CopyToClipboard style={{ width: "100%" }}>
                        <Hr.XXSmall.N60 />
                    </CopyToClipboard>
                    <Spacer />
                    <RenderLinksWithTooltips>
                        <a href="/component-library">Render links with tooltips</a>
                    </RenderLinksWithTooltips>
                </Block>
                <Block title="Formatted Date, Time and DateTime">
                    <CopyToClipboard>
                        <FormattedDate.Default date={new Date()} />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <FormattedDateTime.Default date={new Date()} />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <FormattedDate.Default date={addDays(new Date(), 365)} />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <FormattedDateTime.Comparison date={addDays(new Date(), 12)} />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <FormattedDateTime.Comparison date={addDays(new Date(), -28)} />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <FormattedDateTime.ComparisonUpTo30Days
                            date={addDays(new Date(), -31)}
                        />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <FormattedDate.Comparison
                            timestamp={{ seconds: 1600000000, nanoseconds: 0 }}
                        />
                    </CopyToClipboard>
                </Block>
                <Block title="Assets">
                    <FlexRowWrap>
                        {Object.values(Assets).map((icon) => (
                            <CopyToClipboard
                                key={icon}
                                className="component-library__preview-block"
                                style={{ display: "flex" }}
                            >
                                <Asset src={icon} style={fitToSize} />
                            </CopyToClipboard>
                        ))}
                    </FlexRowWrap>
                </Block>

                <Block title="Icons">
                    <Subtitle>XSmall</Subtitle>
                    <FlexRowWrap>
                        {Object.entries(Icon).map(([key, Component]) => (
                            <Tooltip key={key} label={`<Icon.${key}.XSmall />`}>
                                <Component.XSmall />
                            </Tooltip>
                        ))}
                    </FlexRowWrap>
                    <Spacer />
                    <Subtitle>Small</Subtitle>
                    <FlexRowWrap>
                        {Object.entries(Icon).map(([key, Component]) => (
                            <Tooltip key={key} label={`<Icon.${key}.Small />`}>
                                <Component.Small />
                            </Tooltip>
                        ))}
                    </FlexRowWrap>
                    <Spacer />

                    <Subtitle>Medium</Subtitle>
                    <FlexRowWrap>
                        {Object.entries(Icon).map(([key, Component]) => (
                            <Tooltip key={key} label={`<Icon.${key}.Medium />`}>
                                <Component.Medium />
                            </Tooltip>
                        ))}
                    </FlexRowWrap>
                    <Spacer />

                    <Subtitle>Medium with fill</Subtitle>
                    <FlexRowWrap>
                        {Object.entries(Icon).map(([key, Component]) => (
                            <Tooltip
                                key={key}
                                label={`<Icon.${key}.Medium.N10.Fill />`}
                            >
                                <Component.Medium.N10.Fill />
                            </Tooltip>
                        ))}
                    </FlexRowWrap>
                    <Spacer />

                    <Subtitle>Pictogram small</Subtitle>
                    <Spacer />

                    <FlexRowWrap>
                        {Object.entries(PictogramIcon).map(([key, Component]) => (
                            <Tooltip
                                key={key}
                                label={`<PictogramIcon.${key}.Small.P95 />`}
                            >
                                <Component.Small.P90 />
                            </Tooltip>
                        ))}
                    </FlexRowWrap>

                    <Spacer />
                    <Subtitle>Pictogram medium</Subtitle>
                    <Spacer />

                    <FlexRowWrap>
                        {Object.entries(PictogramIcon).map(([key, Component]) => (
                            <Tooltip
                                key={key}
                                label={`<PictogramIcon.${key}.Medium.P90 />`}
                            >
                                <Component.Medium.P90 />
                            </Tooltip>
                        ))}
                    </FlexRowWrap>
                    <Spacer />

                    <Spacer />
                    <Subtitle>Pictogram large</Subtitle>
                    <Spacer />

                    <FlexRowWrap>
                        {Object.entries(PictogramIcon).map(([key, Component]) => (
                            <Tooltip
                                key={key}
                                label={`<PictogramIcon.${key}.Large.P90 />`}
                            >
                                <Component.Large.P90 />
                            </Tooltip>
                        ))}
                    </FlexRowWrap>
                    <Spacer />

                    <Subtitle>Colors</Subtitle>
                    <FlexRowWrap>
                        <Tooltip label="<Icon.Send.Medium.N10 />">
                            <Icon.Send.Medium.N10 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.N20 />">
                            <Icon.Send.Medium.N20 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.N50 />">
                            <Icon.Send.Medium.N50 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.N60 />">
                            <Icon.Send.Medium.N60 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.N99 />">
                            <Icon.Send.Medium.N99 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.N100 />">
                            <Icon.Send.Medium.N100 />
                        </Tooltip>
                    </FlexRowWrap>

                    <FlexRowWrap>
                        <Tooltip label="<Icon.Send.Medium.P10 />">
                            <Icon.Send.Medium.P10 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.P20 />">
                            <Icon.Send.Medium.P30 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.P50 />">
                            <Icon.Send.Medium.P50 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.P60 />">
                            <Icon.Send.Medium.P60 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.P80 />">
                            <Icon.Send.Medium.P80 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.P90 />">
                            <Icon.Send.Medium.P90 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.P95 />">
                            <Icon.Send.Medium.P95 />
                        </Tooltip>
                    </FlexRowWrap>

                    <FlexRowWrap>
                        <Tooltip label="<Icon.Send.Medium.S10 />">
                            <Icon.Send.Medium.S10 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.S30 />">
                            <Icon.Send.Medium.S30 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.S50 />">
                            <Icon.Send.Medium.S50 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.S80 />">
                            <Icon.Send.Medium.S80 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.S90 />">
                            <Icon.Send.Medium.S90 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.S95 />">
                            <Icon.Send.Medium.S95 />
                        </Tooltip>
                    </FlexRowWrap>

                    <FlexRowWrap>
                        <Tooltip label="<Icon.Send.Medium.T10 />">
                            <Icon.Send.Medium.T10 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.T30 />">
                            <Icon.Send.Medium.T30 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.T50 />">
                            <Icon.Send.Medium.T50 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.T80 />">
                            <Icon.Send.Medium.T80 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.T90 />">
                            <Icon.Send.Medium.T90 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.T95 />">
                            <Icon.Send.Medium.T95 />
                        </Tooltip>
                    </FlexRowWrap>

                    <FlexRowWrap>
                        <Tooltip label="<Icon.Send.Medium.E10 />">
                            <Icon.Send.Medium.E10 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.E50 />">
                            <Icon.Send.Medium.E50 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.E60 />">
                            <Icon.Send.Medium.E60 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.E70 />">
                            <Icon.Send.Medium.E70 />
                        </Tooltip>
                        <Tooltip label="<Icon.Send.Medium.E90 />">
                            <Icon.Send.Medium.E90 />
                        </Tooltip>
                    </FlexRowWrap>

                    <FlexRowWrap>
                        <Tooltip label="<PictogramIcon.Send.Medium.N99 />">
                            <PictogramIcon.Send.Medium.N99 />
                        </Tooltip>
                        <Tooltip label="<PictogramIcon.Send.Medium.N90 />">
                            <PictogramIcon.Send.Medium.N90 />
                        </Tooltip>
                        <Tooltip label="<PictogramIcon.Send.Medium.P90 />">
                            <PictogramIcon.Send.Medium.P90 />
                        </Tooltip>
                        <Tooltip label="<PictogramIcon.Send.Medium.S90 />">
                            <PictogramIcon.Send.Medium.S90 />
                        </Tooltip>
                        <Tooltip label="<PictogramIcon.Send.Medium.T90 />">
                            <PictogramIcon.Send.Medium.T90 />
                        </Tooltip>
                        <Tooltip label="<PictogramIcon.Send.Medium />">
                            <PictogramIcon.Send.Medium />
                        </Tooltip>
                        <Tooltip label="<PictogramIcon.Send.Medium.E90 />">
                            <PictogramIcon.Send.Medium.E90 />
                        </Tooltip>
                    </FlexRowWrap>

                    <FlexRowWrap>
                        <Tooltip
                            className="pictogram-icon--hover"
                            label="<PictogramIcon.Send.Medium.N99 />"
                        >
                            <PictogramIcon.Send.Medium.N99 />
                        </Tooltip>
                        <Tooltip
                            className="pictogram-icon--hover"
                            label="<PictogramIcon.Send.Medium.N90 />"
                        >
                            <PictogramIcon.Send.Medium.N90 />
                        </Tooltip>
                        <Tooltip
                            className="pictogram-icon--hover"
                            label="<PictogramIcon.Send.Medium.P90 />"
                        >
                            <PictogramIcon.Send.Medium.P90 />
                        </Tooltip>
                        <Tooltip
                            className="pictogram-icon--hover"
                            label="<PictogramIcon.Send.Medium.S90 />"
                        >
                            <PictogramIcon.Send.Medium.S90 />
                        </Tooltip>
                        <Tooltip
                            className="pictogram-icon--hover"
                            label="<PictogramIcon.Send.Medium.T90 />"
                        >
                            <PictogramIcon.Send.Medium.T90 />
                        </Tooltip>
                        <Tooltip
                            className="pictogram-icon--hover"
                            label="<PictogramIcon.Send.Medium />"
                        >
                            <PictogramIcon.Send.Medium />
                        </Tooltip>
                        <Tooltip
                            className="pictogram-icon--hover"
                            label="<PictogramIcon.Send.Medium.E90 />"
                        >
                            <PictogramIcon.Send.Medium.E90 />
                        </Tooltip>
                    </FlexRowWrap>
                </Block>
                <Block title="Colors">
                    <Subtitle>
                        Surface palette - use for backgrounds and overlays
                    </Subtitle>
                    <GroupBlock>
                        <ColorBlock color="var(--a-ds-surface-lowest)" />
                        <ColorBlock color="var(--a-ds-surface-low)" />
                        <ColorBlock color="var(--a-ds-surface)" />
                        <ColorBlock color="var(--a-ds-surface-high)" />
                        <ColorBlock color="var(--a-ds-surface-highest)" />

                        <ColorBlock color="var(--a-ds-surface-dark" />
                        <ColorBlock color="var(--a-ds-surface-darker" />
                        <ColorBlock color="var(--a-ds-surface-darkest" />

                        <ColorBlock color="var(--a-ds-surface-overlay-dark" />
                        <ColorBlock color="var(--a-ds-surface-overlay-light" />
                    </GroupBlock>

                    <Subtitle>Neutral palette</Subtitle>
                    <GroupBlock>
                        <ColorBlock color="var(--a-ds-color-n-0)" />
                        <ColorBlock color="var(--a-ds-color-n-10)" />
                        <ColorBlock color="var(--a-ds-color-n-20)" />
                        <ColorBlock color="var(--a-ds-color-n-30)" />
                        <ColorBlock color="var(--a-ds-color-n-50)" />
                        <ColorBlock color="var(--a-ds-color-n-60)" />
                        <ColorBlock color="var(--a-ds-color-n-70)" />
                        <ColorBlock color="var(--a-ds-color-n-75)" />
                        <ColorBlock color="var(--a-ds-color-n-80)" />
                        <ColorBlock color="var(--a-ds-color-n-90)" />
                        <ColorBlock color="var(--a-ds-color-n-95)" />
                        <ColorBlock color="var(--a-ds-color-n-99)" />
                        <ColorBlock color="var(--a-ds-color-n-100)" />
                    </GroupBlock>

                    <Subtitle>Primary highlight color</Subtitle>
                    <GroupBlock>
                        <ColorBlock color="var(--a-ds-color-p-10)" />
                        <ColorBlock color="var(--a-ds-color-p-30)" />
                        <ColorBlock color="var(--a-ds-color-p-50)" />
                        <ColorBlock color="var(--a-ds-color-p-60)" />
                        <ColorBlock color="var(--a-ds-color-p-80)" />
                        <ColorBlock color="var(--a-ds-color-p-90)" />
                        <ColorBlock color="var(--a-ds-color-p-95)" />
                    </GroupBlock>

                    <Subtitle>Secondary highlight color</Subtitle>
                    <GroupBlock>
                        <ColorBlock color="var(--a-ds-color-s-10)" />
                        <ColorBlock color="var(--a-ds-color-s-30)" />
                        <ColorBlock color="var(--a-ds-color-s-50)" />
                        <ColorBlock color="var(--a-ds-color-s-80)" />
                        <ColorBlock color="var(--a-ds-color-s-90)" />
                        <ColorBlock color="var(--a-ds-color-s-95)" />
                    </GroupBlock>

                    <Subtitle>Tertiary highlight color</Subtitle>
                    <GroupBlock>
                        <ColorBlock color="var(--a-ds-color-t-10)" />
                        <ColorBlock color="var(--a-ds-color-t-30)" />
                        <ColorBlock color="var(--a-ds-color-t-50)" />
                        <ColorBlock color="var(--a-ds-color-t-80)" />
                        <ColorBlock color="var(--a-ds-color-t-90)" />
                        <ColorBlock color="var(--a-ds-color-t-95)" />
                    </GroupBlock>

                    <Subtitle>Error highlight color</Subtitle>
                    <GroupBlock>
                        <ColorBlock color="var(--a-ds-color-e-10)" />
                        <ColorBlock color="var(--a-ds-color-e-50)" />
                        <ColorBlock color="var(--a-ds-color-e-60)" />
                        <ColorBlock color="var(--a-ds-color-e-70)" />
                        <ColorBlock color="var(--a-ds-color-e-80)" />
                        <ColorBlock color="var(--a-ds-color-e-90)" />
                    </GroupBlock>

                    <Subtitle>Deadline colors</Subtitle>
                    <GroupBlock>
                        <ColorBlock color="var(--a-ds-deadline-background-color)" />
                        <ColorBlock color="var(--a-ds-deadline-overdue)" />
                        <ColorBlock color="var(--a-ds-deadline-priority1)" />
                        <ColorBlock color="var(--a-ds-deadline-priority2)" />
                        <ColorBlock color="var(--a-ds-deadline-priority3)" />
                        <ColorBlock color="var(--a-ds-deadline-priority4)" />
                    </GroupBlock>
                </Block>
                <Block title="Shadows">
                    <div style={{ display: "flex", flexFlow: "wrap" }}>
                        <ShadowBlock boxShadow="var(--a-ds-elevate-1)" />
                        <ShadowBlock boxShadow="var(--a-ds-elevate-2)" />
                        <ShadowBlock boxShadow="var(--a-ds-elevate-3)" />
                        <ShadowBlock boxShadow="var(--a-ds-elevate-4)" />
                        <ShadowBlock boxShadow="var(--a-ds-elevate-5)" />
                    </div>
                </Block>
                <Block title="Buttons">
                    <CopyToClipboard>
                        <Button label="Button" onClick={undefined} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.N10 label="Button.N10" onClick={undefined} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.P50 label="Button.P50" onClick={undefined} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.P90 label="Button.P90" onClick={undefined} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.N90 label="Button.N90" onClick={undefined} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.E90 label="Button.E90" onClick={undefined} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.Ghost label="Button.Ghost" onClick={undefined} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.Plain label="Button.Plain" onClick={undefined} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.Plain onClick={undefined}>
                            <Icon.NotificationAdd.Small />
                            <Label.Medium.Light>
                                Button.Plain with icon
                            </Label.Medium.Light>
                        </Button.Plain>
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.Plain
                            label="Button.Plain showLoadingSpinner"
                            onClick={undefined}
                            showLoadingSpinner
                        />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.Plain
                            label="Button.Plain disabled"
                            onClick={undefined}
                            disabled
                        />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.Primary label="Button.Primary" onClick={undefined} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.PrimaryAttention
                            label="Button.PrimaryAttention"
                            onClick={undefined}
                        />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.Secondary
                            label="Button.Secondary"
                            onClick={undefined}
                        />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.Secondary
                            label="Button.Secondary showLoadingSpinner"
                            onClick={undefined}
                            showLoadingSpinner
                        />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.Secondary
                            label="Button.Secondary disabled"
                            onClick={undefined}
                            disabled
                        />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Button.Secondary onClick={undefined}>
                            <Icon.NotificationAdd.Small />
                            <Label.Medium.Light>
                                Button.Secondary with icon
                            </Label.Medium.Light>
                        </Button.Secondary>
                    </CopyToClipboard>
                    <Spacer />
                    <GroupBlock>
                        <CopyToClipboard>
                            <ChevronRoundButton
                                direction="up-or-down"
                                onToggleClick={(open) => {
                                    console.log(`Clicked state - is down - ${open}`);
                                }}
                            />
                        </CopyToClipboard>
                        <Spacer />
                        <CopyToClipboard>
                            <ChevronRoundButton
                                direction="left-or-right"
                                onToggleClick={(open) => {
                                    console.log(`Clicked state - is left - ${open}`);
                                }}
                            />
                        </CopyToClipboard>
                    </GroupBlock>
                    <Spacer />
                    <CopyToClipboard>
                        <BackButton onBackClick={undefined} />
                    </CopyToClipboard>
                    <Spacer />
                    <hr />
                    <Subtitle>Pill Buttons</Subtitle>
                    <Spacer />
                    <CopyToClipboard>
                        <ButtonPill
                            onClick={() => {}}
                            text="Button text"
                            dataTooltip="Tooltip info"
                            icon="Check"
                        />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <ButtonPill
                            onClick={() => {
                                console.log("clicked");
                            }}
                            text="Button text"
                            dataTooltip="Tooltip info"
                            loading
                        />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <ButtonPill
                            onClick={() => {
                                console.log("clicked");
                            }}
                            text="Button text"
                            dataTooltip="Tooltip info"
                        />
                    </CopyToClipboard>
                </Block>
                <Block title="Flexbox layout">
                    <Subtitle>
                        <Label.Medium.SemiBold>
                            Flex.Row gap=&apos;&apos;medium&apos;&apos;
                        </Label.Medium.SemiBold>
                    </Subtitle>
                    <Spacer />

                    <Flex.Row gap="medium">
                        <Label.Medium.Light>Item</Label.Medium.Light>
                        <Label.Medium.Light>Item</Label.Medium.Light>
                        <Label.Medium.Light>Item</Label.Medium.Light>
                        <Label.Medium.Light>Item</Label.Medium.Light>
                        <Label.Medium.Light>Item</Label.Medium.Light>
                    </Flex.Row>

                    <Spacer />

                    <Subtitle>
                        <Label.Medium.SemiBold>
                            Flex.Column gap=&apos;&apos;large&apos;&apos;
                        </Label.Medium.SemiBold>
                    </Subtitle>
                    <Spacer />

                    <Flex.Column gap="large">
                        <Label.Medium.Light>Item</Label.Medium.Light>
                        <Label.Medium.Light>Item</Label.Medium.Light>
                        <Label.Medium.Light>Item</Label.Medium.Light>
                        <Label.Medium.Light>Item</Label.Medium.Light>
                    </Flex.Column>

                    <Spacer />

                    <Subtitle>
                        <Label.Medium.SemiBold>
                            FlexInline gap=&apos;&apos;small&apos;&apos;
                        </Label.Medium.SemiBold>
                    </Subtitle>
                    <Spacer />

                    <FlexInline gap="small">
                        <Icon.Sensors.Small />
                        <Label.Medium.Light>Icon and text</Label.Medium.Light>
                        <InlineTextDelimiter />
                        <Icon.LocationOn.Small />
                        <Label.Medium.Light>Icon and text</Label.Medium.Light>
                        <InlineTextDelimiter />
                        <Paragraph>Paragraph</Paragraph>
                    </FlexInline>

                    <Spacer />
                </Block>

                <OverlayExamples />

                <Block title="Request Card" className="desktop">
                    <List.Tab>
                        <RequestCard
                            request={mockRequest}
                            currentAgent={mockAgent}
                            layout="horizontal"
                        />
                    </List.Tab>
                    <Spacer />
                    <div style={{ maxWidth: 335 }}>
                        <RequestCard
                            request={mockRequest}
                            currentAgent={mockAgent}
                            layout="vertical"
                        />
                    </div>
                </Block>

                <Block title="Request Snippet">
                    <Subtitle>New</Subtitle>
                    <Spacer />
                    <RequestSnippet request={mockRequest} confirmedBookings={[]} />
                    <Spacer />

                    <Subtitle>Draft</Subtitle>
                    <Spacer />
                    <RequestSnippet request={mockRequestDraft} confirmedBookings={[]} />
                    <Spacer />

                    <Subtitle>Active - Open</Subtitle>
                    <Spacer />
                    <RequestSnippet
                        request={{
                            ...mockRequestActive,
                            nextDeadlineAt: timestampFromDate(addHours(new Date(), 1)),
                            nextDeadlineStep: "send_proposal",
                        }}
                        confirmedBookings={[]}
                    />
                    <Spacer />

                    <Subtitle>Active - Confirmed</Subtitle>
                    <Spacer />
                    <RequestSnippet
                        request={mockRequestConfirmed}
                        confirmedBookings={[
                            {
                                request: mockRequest.id,
                                bookings: [mockConfirmedBooking],
                            },
                        ]}
                    />
                    <Spacer />

                    <Subtitle>Closed</Subtitle>
                    <Spacer />
                    <RequestSnippet
                        request={{
                            ...mockRequestClosed,
                            nextDeadlineAt: timestampFromDate(addDays(new Date(), 2)),
                            nextDeadlineStep: "revisit_request",
                        }}
                        confirmedBookings={[
                            {
                                request: mockRequest.id,
                                bookings: [mockConfirmedBooking],
                            },
                        ]}
                    />
                </Block>

                <Block title="Article Card">
                    <Subtitle>L</Subtitle>
                    <Spacer />
                    <ArticleThumbnail size="L" article={catalogItemExample} />
                    <Spacer />

                    <Subtitle>M</Subtitle>
                    <Spacer />
                    <ArticleThumbnail size="M" article={catalogItemExample} />
                    <Spacer />

                    <Subtitle>S</Subtitle>
                    <Spacer />
                    <ArticleThumbnail size="S" article={catalogItemExample} />
                </Block>

                <Block title="Sortable list">
                    <Subtitle>With default drag over placeholder</Subtitle>
                    <SortableExample
                        initialItems={[
                            { id: "one", name: "One" },
                            { id: "two", name: "Two" },
                            { id: "three", name: "Three" },
                            { id: "four", name: "Four" },
                        ]}
                        renderItem={(item) => (
                            <List.Item id={item.id}>
                                <Flex.Row gap="medium">
                                    <Icon.DragIndicator.Small.N10 />
                                    {item.name}
                                </Flex.Row>
                            </List.Item>
                        )}
                        onChange={(items) => {
                            console.log(`Sortable list new order:`, items);
                        }}
                    />

                    <Spacer />

                    <Subtitle>With custom drag over placeholder</Subtitle>
                    <SortableExample
                        initialItems={[
                            { id: "one", name: "One" },
                            { id: "two", name: "Two" },
                            { id: "three", name: "Three" },
                            { id: "four", name: "Four" },
                        ]}
                        renderItem={(item) => (
                            <List.Item id={item.id}>
                                <Flex.Row gap="medium">
                                    <Icon.DragIndicator.Small.N10 />
                                    {item.name}
                                </Flex.Row>
                            </List.Item>
                        )}
                        renderDragOver={() => <Hr />}
                        onChange={(items) => {
                            console.log(`Sortable list new order:`, items);
                        }}
                    />
                </Block>
                <Block title="Avatar">
                    <Subtitle>Initials for any firstname&lastname</Subtitle>
                    <CopyToClipboard>
                        <Initials firstName="Eelis" lastName="Example" size="big" />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Initials firstName="Eelis" lastName="Example" size="mid" />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <Initials firstName="Eelis" lastName="Example" size="small" />
                    </CopyToClipboard>
                    <Spacer />

                    <Subtitle>Member</Subtitle>
                    <CopyToClipboard>
                        <MemberAvatar
                            memberId="6c7c2f6e92f69262d10724997fadd82462640e12510ccf33589954c52e8edaec"
                            size="big"
                        />
                    </CopyToClipboard>

                    <CopyToClipboard>
                        <MemberAvatar
                            memberId="6c7c2f6e92f69262d10724997fadd82462640e12510ccf33589954c52e8edaec"
                            size="mid"
                        />
                    </CopyToClipboard>

                    <CopyToClipboard>
                        <MemberAvatar
                            memberId="6c7c2f6e92f69262d10724997fadd82462640e12510ccf33589954c52e8edaec"
                            size="small"
                        />
                    </CopyToClipboard>
                    <Spacer />

                    <Subtitle>Albertine Service agent</Subtitle>
                    <CopyToClipboard>
                        <AgentAvatar agentId="AlbertineServiceAgent" size="big" />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <AgentAvatar agentId="AlbertineServiceAgent" size="mid" />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <AgentAvatar agentId="AlbertineServiceAgent" size="small" />
                    </CopyToClipboard>
                    <Spacer />

                    <Subtitle>Agent</Subtitle>

                    <CopyToClipboard>
                        <AgentAvatar
                            agentId="dS3CcpFgNmoXo2u8QfsqAYmiBlhg"
                            size="big"
                        />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <AgentAvatar
                            agentId="dS3CcpFgNmoXo2u8QfsqAYmiBlhg"
                            size="mid"
                        />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <AgentAvatar
                            agentId="dS3CcpFgNmoXo2u8QfsqAYmiBlhg"
                            size="small"
                        />
                    </CopyToClipboard>
                    <Spacer />

                    <Subtitle>Agent with secondary</Subtitle>
                    <CopyToClipboard>
                        <AgentAvatarWithSecondary
                            primaryAgentId="je6g30G187RBdcQV2iPNrJvx2rxQ"
                            secondaryAgentId="dS3CcpFgNmoXo2u8QfsqAYmiBlhg"
                        />
                    </CopyToClipboard>

                    <Spacer />
                    <Subtitle>Agent avatar listing</Subtitle>
                    <CopyToClipboard>
                        <AgentAvatarList
                            agents={
                                [
                                    {
                                        id: "je6g30G187RBdcQV2iPNrJvx2rxQ",
                                        firstName: "Albertine",
                                    },
                                    {
                                        id: "dS3CcpFgNmoXo2u8QfsqAYmiBlhg",
                                        firstName: "Tine",
                                    },
                                ] as Agent[]
                            }
                        />
                    </CopyToClipboard>
                </Block>
                <Block title="Date picker">
                    {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                    <DatePicker
                        showPopperArrow={false}
                        selected={exampleDate}
                        className="react-datepicker__calendar-icon"
                        onChange={setExampleDate}
                        placeholderText="dd/MM/yyyy"
                        dateFormat="dd/MM/yyyy"
                        calendarStartDay={1}
                    />
                    <Spacer />
                    {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                    <DatePicker
                        showPopperArrow={false}
                        selected={exampleDate}
                        onChange={setExampleDate}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        timeCaption="Time"
                        dateFormat="dd/MM/yyyy, HH:mm"
                        placeholderText="dd/MM/yyyy, HH:mm"
                        calendarStartDay={1}
                    />
                    <Spacer />
                    {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                    <DatePicker
                        showPopperArrow={false}
                        selected={exampleDate}
                        onChange={setExampleDate}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={15}
                        timeCaption="Time"
                        timeFormat="HH:mm"
                        dateFormat="HH:mm"
                        placeholderText="HH:mm"
                        calendarStartDay={1}
                    />
                    <Spacer />
                    {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                    <DatePicker
                        inline
                        selected={exampleDate}
                        className="react-datepicker__calendar-icon"
                        onChange={setExampleDate}
                        placeholderText="dd/MM/yyyy"
                        dateFormat="dd/MM/yyyy"
                        calendarStartDay={1}
                    />
                    <Spacer />
                    <div>
                        <Button.Plain
                            label="Show LocalDateTimePicker"
                            onClick={() => {
                                setShowLocalDateTimePicker(true);
                            }}
                        />
                        {showLocalDateTimePicker && (
                            <LocalDateTimePicker
                                onConfirm={() => {
                                    setShowLocalDateTimePicker(false);
                                }}
                                onCancel={() => {
                                    setShowLocalDateTimePicker(false);
                                }}
                            />
                        )}
                    </div>
                </Block>
                <Block title="Loading Spinner">
                    <CopyToClipboard>
                        <LoadingSpinner.Small />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <LoadingSpinner.Medium />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <LoadingSpinner.Big />
                    </CopyToClipboard>
                    <Spacer />
                </Block>

                <Block title="Tooltips">
                    <GroupBlock>
                        <CopyToClipboard>
                            <Tooltip label="Top" placement="top">
                                Top
                            </Tooltip>
                        </CopyToClipboard>
                        <Spacer />
                        <Spacer />
                        <CopyToClipboard>
                            <Tooltip label="Bottom" placement="bottom">
                                Bottom
                            </Tooltip>
                        </CopyToClipboard>
                        <Spacer />
                        <Spacer />
                        <CopyToClipboard>
                            <Tooltip label="Left" placement="left">
                                Left
                            </Tooltip>
                        </CopyToClipboard>
                        <Spacer />
                        <Spacer />
                        <CopyToClipboard>
                            <Tooltip label="Right" placement="right">
                                Right
                            </Tooltip>
                        </CopyToClipboard>
                        <Spacer />
                        <Spacer />
                        <CopyToClipboard>
                            <Tooltip label="Top-left" placement="top-left">
                                Top-left
                            </Tooltip>
                        </CopyToClipboard>
                        <Spacer />
                        <Spacer />
                        <CopyToClipboard>
                            <Tooltip label="Top-right" placement="top-right">
                                Top-right
                            </Tooltip>
                        </CopyToClipboard>
                    </GroupBlock>
                    <Spacer />
                </Block>
                <Block title="Dropdown">
                    <CopyToClipboard>
                        <Dropdown
                            label="Dropdown:"
                            entries={Object.entries({
                                value1: "Value 1",
                                value2: "Value 2",
                                value3: "Value 3",
                                value4: "Value 4",
                            })}
                            onChange={(value) => {
                                console.log(value);
                            }}
                        />
                    </CopyToClipboard>
                </Block>
                <Block title="Input">
                    <Input.TextField
                        value="Input.TextField"
                        onChange={(value) => {
                            console.log(value);
                        }}
                        onBlur={(value) => {
                            console.log(value);
                        }}
                    />
                    <Spacer />
                    <Flex.Row gap="small">
                        <Label.Medium.N50>Label:</Label.Medium.N50>
                        <Input.TextField
                            value="<Label> and <Input.Textfield> wrapped inside a <Flex.Row>"
                            placeholder="Placeholder..."
                            onChange={(value) => {
                                console.log(value);
                            }}
                            onBlur={(value) => {
                                console.log(value);
                            }}
                        />
                    </Flex.Row>
                    <Spacer />
                    <Input.TextField
                        value=""
                        label="Label:"
                        placeholder="Placeholder..."
                        onChange={(value) => {
                            console.log(value);
                        }}
                        onBlur={(value) => {
                            console.log(value);
                        }}
                    />
                    <Spacer />
                    <Input.TextField
                        value=""
                        label="Label:"
                        placeholder="Placeholder..."
                        trim
                        lowercase
                        supportingText="Supporting text, trim and lowercase enabled"
                        onChange={(value) => {
                            console.log(value);
                        }}
                        onBlur={(value) => {
                            console.log(value);
                        }}
                    />
                    <Spacer />

                    <Input.TextField
                        value="Invalid value"
                        label="Label:"
                        placeholder="Placeholder..."
                        supportingText="Example with invalid value and validation"
                        isValid={(value) => value !== "Invalid value"}
                        maxLength={20}
                        onChange={(value) => {
                            console.log(value);
                        }}
                        onBlur={(value) => {
                            console.log(value);
                        }}
                    />
                    <Spacer />
                    <Input.TextField
                        highlight
                        value="Highlight"
                        label="Label:"
                        placeholder="Placeholder..."
                        onBlur={(value) => {
                            console.log(value);
                        }}
                    />
                    <Spacer />
                    <Input.TextField
                        disabled
                        value="Disabled"
                        label="Label:"
                        placeholder="Placeholder..."
                    />
                    <Spacer />
                    <Input.TextField
                        readOnly
                        value="ReadOnly"
                        label="Label:"
                        placeholder="Placeholder..."
                    />
                    <Spacer />
                    <Input.TextField
                        value=""
                        label="Loading spinner example:"
                        supportingText="Make some changes and click outside"
                        placeholder="Click to write something..."
                        onBlur={async () => wait(3000)}
                    />
                    <Spacer />
                    <Input.Email
                        label="Email:"
                        value="example@example.com"
                        supportingText="Input.Email component example"
                    />
                    <Spacer />
                    <hr />
                    <Spacer />
                    <Input.TextArea
                        value="Input.Textarea"
                        label="TextArea:"
                        placeholder="Placeholder..."
                        supportingText="TextArea with autoResize"
                        onChange={(value) => {
                            console.log(value);
                        }}
                        onBlur={(value) => {
                            console.log(value);
                        }}
                        autoResize
                    />
                    <Spacer />
                    <Input.TextArea
                        value=""
                        label="TextArea:"
                        placeholder="Placeholder..."
                        supportingText="With autoresize and removeLineBreaks"
                        removeLineBreaks
                        autoResize
                        onChange={(value) => {
                            console.log(value);
                        }}
                        onBlur={(value) => {
                            console.log(value);
                        }}
                    />
                    <Spacer />
                    <Input.TextArea
                        value=""
                        label="Loading spinner example:"
                        placeholder="Click to write something..."
                        supportingText="Make some changes and click outside"
                        onBlur={async () => wait(3000)}
                    />
                    <Spacer />
                    <Input.Checkbox
                        label="Option"
                        checked={false}
                        supportingText="This checkbox is not selected by default"
                    />
                    <Spacer />
                    <Input.Checkbox
                        label="Option"
                        checked
                        supportingText="This checkbox is selected by default"
                    />
                    <Spacer />
                    <Input.Checkbox
                        label="Option"
                        checked={false}
                        disabled
                        supportingText="This checkbox is disabled"
                    />
                    <Spacer />
                    <Input.Checkbox
                        label="Option"
                        checked={false}
                        readOnly
                        supportingText="This checkbox is readonly"
                    />
                    <Spacer />
                    <Input.Checkbox
                        label="Option"
                        checked={false}
                        highlight
                        supportingText="This checkbox is highlighted and not selected by default"
                    />
                    <Spacer />
                    <Input.Checkbox
                        label="Option"
                        checked
                        highlight
                        supportingText="This checkbox is highlighted and is selected by default"
                    />
                    <Spacer />
                    <Input.Checkbox
                        label="Option"
                        checked={false}
                        isValid={() => false}
                        supportingText="This checkbox is invalid"
                    />
                    <Spacer />
                    <AutocompleteDropdown
                        label="Test autocomplete dropdown"
                        items={[
                            { id: "test1", text: "Test 1", selected: false },
                            { id: "test2", text: "Test 2", selected: false },
                            { id: "test3", text: "Test 3", selected: false },
                            { id: "abc1", text: "Abc 1", selected: false },
                            { id: "abc2", text: "Abc 2", selected: false },
                            { id: "abc3", text: "Abc 3", selected: false },
                        ]}
                        onChange={(selectedItems) => {
                            console.log(
                                "AutocompleteDropdown.onChange.selectedItems",
                                selectedItems,
                            );
                        }}
                    />
                </Block>
                <Block title="Editable Input Fields">
                    <CopyToClipboard>
                        <EditableInputField
                            label="EditableInputField:"
                            initialValue=""
                            validate={undefined}
                            onChange={(value) => {
                                console.log(value);
                            }}
                        />
                    </CopyToClipboard>
                    <CopyToClipboard>
                        <EditableTextarea
                            label="EditableTextarea:"
                            placeholder="Placeholder..."
                            initialValue=""
                            height={100}
                            onChange={(value) => {
                                console.log(value);
                            }}
                        />
                    </CopyToClipboard>
                </Block>

                <FormExamples />

                <Block title="Autocomplete examples">
                    <AutocompleteExampleWithMembers />
                </Block>

                <Block title="Rounded Selection Toggle">
                    <CopyToClipboard>
                        <RoundedSelectionToggle
                            options={[
                                { label: "Default", value: "default" },
                                { label: "On", value: "on" },
                                { label: "Off", value: "off" },
                            ]}
                            initialValue="default"
                            onChange={(value) => {
                                console.log(value);
                            }}
                        />
                    </CopyToClipboard>
                </Block>
                <Block title="TicketNumber">
                    <CopyToClipboard>
                        <TicketNumber ticketNumber={1} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <TicketNumber ticketNumber={10} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <TicketNumberSmall ticketNumber={100} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <TicketNumberSmall ticketNumber={1000} />
                    </CopyToClipboard>
                </Block>
                <Block title="Highlight">
                    <Highlight label="This is a label with highlight" />
                    <Spacer />
                    <Highlight label="This is a label with highlight and a lot longer, a lot longer,a lot longer,a lot longer,a lot longer,a lot longer,a lot longer text" />
                </Block>
                <Block title="Alert Notification">
                    <CopyToClipboard>
                        <AlertNotification>
                            The kids aren&apos;t alright.
                        </AlertNotification>
                    </CopyToClipboard>
                </Block>
                <Block title="Tag">
                    <CopyToClipboard>
                        <Tag>Default Tag</Tag>
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Tag.Small.N90>Send status update</Tag.Small.N90>
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <Tag.Small.N90 disabled>Send status update</Tag.Small.N90>
                    </CopyToClipboard>
                    <Spacer />
                    <div style={{ display: "flex", gap: "10px" }}>
                        <CopyToClipboard>
                            <Tag.XXSmall.N80>Tag.N80</Tag.XXSmall.N80>
                        </CopyToClipboard>
                        <CopyToClipboard>
                            <Tag.XXSmall.N90>Tag.N90</Tag.XXSmall.N90>
                        </CopyToClipboard>
                        <CopyToClipboard>
                            <Tag.XXSmall.T90>Tag.T90</Tag.XXSmall.T90>
                        </CopyToClipboard>
                        <CopyToClipboard>
                            <Tag.XXSmall.P80>Tag.P80</Tag.XXSmall.P80>
                        </CopyToClipboard>
                        <CopyToClipboard>
                            <Tag.XXSmall.P90>Tag.P90</Tag.XXSmall.P90>
                        </CopyToClipboard>
                        <CopyToClipboard>
                            <Tag.XXSmall.Y80>Tag.Y80</Tag.XXSmall.Y80>
                        </CopyToClipboard>
                        <CopyToClipboard>
                            <Tag.XXSmall.R80>Tag.R80</Tag.XXSmall.R80>
                        </CopyToClipboard>
                    </div>
                </Block>
                <Block title="DeadlineTag">
                    <CopyToClipboard>
                        <DeadlineTag deadlineAt={new Date()} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <DeadlineTag deadlineAt={addMinutes(new Date(), 30)} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <DeadlineTag deadlineAt={addHours(new Date(), 2)} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <DeadlineTag deadlineAt={addHours(new Date(), 12)} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <DeadlineTag deadlineAt={addDays(new Date(), 2)} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <DeadlineTag deadlineAt={addDays(new Date(), 30)} />
                    </CopyToClipboard>
                </Block>
                <Block title="TimeTag">
                    <CopyToClipboard>
                        <TimeTag time={addMinutes(new Date(), 30)} />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <TimeTag label="Added" time={addMinutes(new Date(), -30)} />
                    </CopyToClipboard>
                </Block>
                <Block title="Unread Messages Indicator">
                    <CopyToClipboard>
                        <UnreadMessagesIndicator count={1} type="unread" />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <UnreadMessagesIndicator count={100} type="overdue" />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <UnreadMessagesIndicator count={100} type="overdue-soon" />
                    </CopyToClipboard>
                    <Spacer />
                    <CopyToClipboard>
                        <UnreadMessagesIndicator count={1000} type="info" />
                    </CopyToClipboard>
                </Block>

                <Block title="Menu button">
                    <Subtitle>Menu button</Subtitle>
                    <Spacer />

                    <MenuButton
                        button={
                            <Flex.Row gap="medium" className="pictogram-icon--hover">
                                <PictogramIcon.Add.Medium.N90 />
                                <Label.Medium.SemiBold.N10>
                                    Show
                                </Label.Medium.SemiBold.N10>{" "}
                            </Flex.Row>
                        }
                        options={[
                            {
                                label: "Edit",
                                icon: "Edit",
                                onClick: () => {
                                    alert("on edit click");
                                },
                            },
                            {
                                label: "Delete",
                                icon: "Edit",
                                onClick: () => {
                                    alert("on delete click");
                                },
                            },
                        ]}
                    />

                    <Spacer />
                </Block>

                <SearchFilterExamples />

                <Block title="Search articles">
                    <Subtitle>Search published articles</Subtitle>
                    <Spacer />
                    <ArticleSearchInput
                        label="Keyword"
                        searchMode="only-published"
                        onSelect={(selection) => {
                            console.log(`Selected: ${selection.name}`);
                        }}
                    />

                    <Spacer />

                    <Subtitle>Search all articles</Subtitle>
                    <Spacer />
                    <ArticleSearchInput
                        label="Keyword"
                        searchMode="all"
                        onSelect={(selection) => {
                            console.log(`Selected: ${selection.name}`);
                        }}
                    />
                </Block>

                <Block title="Divider">
                    <Divider margin={{ top: 8, bottom: 8 }} />
                    <Spacer />
                    <Divider margin={{ top: 8, bottom: 8 }} variant="dotted" />
                    <Spacer />
                    <Divider axis="vertical" margin={{ top: 8, bottom: 8 }} />
                    <Spacer />
                    <Divider
                        axis="vertical"
                        margin={{ top: 8, bottom: 8 }}
                        variant="dotted"
                    />
                </Block>
                <Block title="Image Gallery">
                    <ImageGallery
                        photos={[
                            {
                                url: "https://s3.amazonaws.com/a.storyblok.com/f/149550/1024x683/ab7763be8e/thyme-hero-shot-the-pool-1024x683.jpg",
                                name: "image",
                                id: "1",
                            },
                            {
                                url: "https://s3.amazonaws.com/a.storyblok.com/f/149550/1024x767/fe89ed4a84/thyme-ox-barn-terrace-1024x767.jpg",
                                name: "image",
                                id: "2",
                            },
                            {
                                url: "https://s3.amazonaws.com/a.storyblok.com/f/149550/1024x683/ab7763be8e/thyme-hero-shot-the-pool-1024x683.jpg",
                                name: "image",
                                id: "3",
                            },
                        ]}
                        height="200px"
                        width="260px"
                        barsDirection="center"
                    />
                </Block>
                <Block title="App Components" className="app-components">
                    <AppHeading1>App Heading 1</AppHeading1>
                    <AppHeading3>App Heading 3</AppHeading3>
                    <AppParagraph>App paragraph.</AppParagraph>

                    <AppParagraphs
                        text={`Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
        Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.`}
                    />
                    <AppButton>App button</AppButton>
                    <AppDashedLine />
                </Block>
                <Block title="Toast Message">
                    <ToastMessages />
                    <div>
                        <div>
                            <Button.Plain
                                label="Trigger toast message"
                                onClick={() => {
                                    EventEmitter.emit({
                                        name: "showToastMessage",
                                        params: {
                                            message: "Example message",
                                            type: "info",
                                        },
                                    });
                                }}
                            />
                            <Spacer />
                            <Button.Plain
                                label="Trigger error toast message"
                                onClick={() => {
                                    EventEmitter.emit({
                                        name: "showToastMessage",
                                        params: {
                                            message: "Example error message",
                                            type: "error",
                                        },
                                    });
                                }}
                            />
                            <Spacer />
                            <Button.Plain
                                label="Trigger notification toast message"
                                onClick={() => {
                                    EventEmitter.emit({
                                        name: "showToastMessage",
                                        params: {
                                            title: "Example message",
                                            subtitle: "Example message",
                                            agent: "AlbertineServiceAgent",
                                            link: "#",
                                            type: "notification",
                                        },
                                    });
                                }}
                            />
                        </div>
                        <Spacer />
                        <div>
                            <Button.Plain
                                label="Trigger request overdue toast message"
                                onClick={() => {
                                    EventEmitter.emit({
                                        name: "showToastMessage",
                                        params: {
                                            title: "Request title",
                                            subtitle: "Send status update",
                                            agent: "AlbertineServiceAgent",
                                            link: "#",
                                            type: "notification",
                                            color: deadlineToCSSColor(new Date()),
                                            deadlineAt: new Date(),
                                        },
                                    });
                                }}
                            />
                            <Spacer />
                            <Button.Plain
                                label="Trigger request overdue in 30 minutes"
                                onClick={() => {
                                    EventEmitter.emit({
                                        name: "showToastMessage",
                                        params: {
                                            title: "Request title",
                                            subtitle: "Send status update",
                                            agent: "AlbertineServiceAgent",
                                            link: "#",
                                            type: "notification",
                                            color: deadlineToCSSColor(
                                                addMinutes(new Date(), 30),
                                            ),
                                            deadlineAt: addMinutes(new Date(), 30),
                                        },
                                    });
                                }}
                            />
                            <Spacer />
                            <Button.Plain
                                label="Trigger request overdue in 60 minutes"
                                onClick={() => {
                                    EventEmitter.emit({
                                        name: "showToastMessage",
                                        params: {
                                            title: "Request title",
                                            subtitle: "Send status update",
                                            agent: "AlbertineServiceAgent",
                                            link: "#",
                                            type: "notification",
                                            color: deadlineToCSSColor(
                                                addMinutes(new Date(), 60),
                                            ),
                                            deadlineAt: addMinutes(new Date(), 60),
                                        },
                                    });
                                }}
                            />
                        </div>
                    </div>
                    <Spacer />
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <ToastMessageInfoView
                            toast={{ message: "Succesful", type: "info" }}
                        />
                        <Spacer />
                        <ToastMessageInfoView
                            toast={{
                                message: "Something went wrong",
                                type: "error",
                            }}
                        />
                        <Spacer />
                        <ToastMessageNotificationView
                            toast={{
                                type: "notification",
                                title: "Could you check the availability for this item? Long title testing. Long title testing. Long title testing. Long title testing. Long title testing. Long title testing.",
                                subtitle:
                                    "Tine Albert tagged you in request name. Long title testing Long title testing Long title testing Long title testing",
                                agent: "AlbertineServiceAgent",
                                link: "#",
                                color: "var(--a-ds-color-s-80)",
                            }}
                        />
                        <Spacer />
                        <ToastMessageNotificationView
                            toast={{
                                type: "notification",
                                title: "New message from member...",
                                subtitle: "Mary Member · Request",
                                member: "6c7c2f6e92f69262d10724997fadd82462640e12510ccf33589954c52e8edaec",
                                link: "#",
                                color: "var(--a-ds-color-s-30)",
                            }}
                        />
                    </div>
                </Block>
                <Block title="HoverMenu">
                    <HoverMenu>
                        <HoverMenuItem>
                            <Tooltip label="Copy link">
                                <Icon.Link.Medium.N20 />
                            </Tooltip>
                        </HoverMenuItem>
                        <HoverMenuItem>
                            <Tooltip label="Resend notification">
                                <Icon.Notifications.Medium.N20 />
                            </Tooltip>
                        </HoverMenuItem>
                        <HoverMenuItem>
                            <Tooltip label="Edit topic">
                                <Icon.Label.Medium.N20 />
                            </Tooltip>
                        </HoverMenuItem>
                        <HoverMenuItem>
                            <Tooltip label="Edit message">
                                <Icon.Edit.Medium.N20 />
                            </Tooltip>
                        </HoverMenuItem>
                        <HoverMenuItem>
                            <Tooltip label="Delete message">
                                <Icon.Delete.Medium.E50 />
                            </Tooltip>
                        </HoverMenuItem>
                        <HoverMenuItem>
                            <Tooltip label="New request">
                                <Icon.Add.Medium.P50 />
                            </Tooltip>
                        </HoverMenuItem>
                    </HoverMenu>
                </Block>
                <Block title="Chat components">
                    <GroupBlock>
                        <EmojiReaction
                            emoji="👍"
                            tooltip="I like this"
                            variant={ReactionVariant.Like}
                        />
                        <Spacer />
                        <EmojiReaction
                            emoji="👎"
                            tooltip="I like this"
                            variant={ReactionVariant.Dislike}
                        />
                    </GroupBlock>
                </Block>
                <Block title="Message Attachment Preview">
                    <GroupBlock>
                        <MessageAttachmentPreview
                            title="Title"
                            subtitle="Subtitle"
                            image="https://s3.amazonaws.com/a.storyblok.com/f/149550/1024x683/ab7763be8e/thyme-hero-shot-the-pool-1024x683.jpg"
                            onClick={() => {
                                console.log("onAdd");
                            }}
                            onRemove={() => {
                                console.log("onRemove");
                            }}
                        />
                        <Spacer />
                        <MessageAttachmentPreview
                            image="https://s3.amazonaws.com/a.storyblok.com/f/149550/1024x683/ab7763be8e/thyme-hero-shot-the-pool-1024x683.jpg"
                            onClick={() => {
                                console.log("onAdd");
                            }}
                            onRemove={() => {
                                console.log("onRemove");
                            }}
                        />
                    </GroupBlock>
                </Block>
                <Block title="Review components">
                    <MemberRating
                        maxRating={5}
                        userRating={3}
                        message="Service and food both great. However I do wish that the curtains had been blue instead of red and that the layout of the restaurant had been rectangular rather than circular. I doubt a feedback would really be this long but still good to keep in mind that this might occur. maybe we could do a popup for reading the entire thing if it gets super long, but perhaps that’s a future problem?"
                    />
                </Block>
                <Block title="Generated options selector">
                    <GeneratedOptionsSelector
                        isLoading={false}
                        options={[
                            "AI Generated response",
                            "AI Generated response 2",
                            "AI Generated response 3",
                            "AI Generated response 4",
                            "AI Generated response 4. ".repeat(50),
                        ]}
                        onGenerateMoreOptions={async () => {
                            await wait(2000);
                        }}
                        onSelectOption={(option) => {
                            console.log(option);
                        }}
                    />
                </Block>

                <Block title="App preview">
                    <AppPreview.WoaArticle
                        catalogItem={{
                            description: "Here is the description",
                            notes: "",
                            source: "albertine",
                            googlePlaceId: "ChIJk-Pwqj8FdkgRsRgIlFZ8FNU",
                            address: "14 Denman St, London W1D 7HJ, UK",
                            addressComponents: [
                                {
                                    longName: "14",
                                    shortName: "14",
                                    types: ["street_number"],
                                },
                                {
                                    longName: "Denman Street",
                                    shortName: "Denman St",
                                    types: ["route"],
                                },
                                {
                                    longName: "London",
                                    shortName: "London",
                                    types: ["postal_town"],
                                },
                                {
                                    longName: "Greater London",
                                    shortName: "Greater London",
                                    types: ["administrative_area_level_2", "political"],
                                },
                                {
                                    longName: "England",
                                    shortName: "England",
                                    types: ["administrative_area_level_1", "political"],
                                },
                                {
                                    longName: "United Kingdom",
                                    shortName: "GB",
                                    types: ["country", "political"],
                                },
                                {
                                    longName: "W1D 7HJ",
                                    shortName: "W1D 7HJ",
                                    types: ["postal_code"],
                                },
                            ],
                            website: "https://somasoho.com/",
                            phoneNumber: "+44 20 3675 1933",
                            googleUrl:
                                "https://maps.google.com/?cid=15354033740905453745",
                            geoPoint: {
                                latitude: 51.5108433,
                                longitude: -0.1352646,
                            },
                            offeringCategories: ["nightlife"],
                            tags: [],
                            supplierData: {},
                            suppliers: [],
                            localInsights: "Here is Albertine insight",
                            benefits: "",
                            storyblokArticleId: null,
                            storyblokArticleSlug: null,
                            distanceToQuery: null,
                            published: false,
                            images: [
                                {
                                    id: "12946251",
                                    url: "https://s3.amazonaws.com/a.storyblok.com/f/261009/956x1200/43155b76e2/111019093-996719167.jpeg",
                                },
                                {
                                    id: "12946261",
                                    url: "https://s3.amazonaws.com/a.storyblok.com/f/261009/901x1200/c2913ce584/742055239-819333908.jpeg",
                                },
                            ],
                            updatedAt: new Date().toISOString(),
                            name: "SOMA Soho",
                            createdAt: new Date().toISOString(),
                            id: "QdHiY2U4uQuUcckea8pR",
                        }}
                    />
                </Block>

                <Block title="Shadcn examples">
                    <ComponentExample />
                </Block>
            </div>

            <div className="component-library--right">
                <TableOfContents />
            </div>
        </div>
    );
};

export default ComponentLibrary;
